/* eslint-disable no-case-declarations */

/**
 * @module FileDocuments
 */

import { Service } from 'rainbow-web-sdk/services/service';
import { LoggerService } from '../core';
import { Bubble, Conversation, RBEvent, User } from '../../models';
import { ConversationRB } from '../../models/conversation.model';
import { ConsumptionData, FileStorageService, FileStorageServiceEvents } from '../files/fileStorage.service';
import { FileDescriptor, RBError, RBErrorCode } from '../../models/common';
import { FileServerService } from '../files/fileServer.service';
import { Subject, Subscription, filter } from 'rxjs';
import { FileDocument, FileDocumentEvents, FileDocumentRB, FileDocumentSearchOptions, FileDocumentSearchSet, FileDocumentSearchSortBy, FileDocumentSearchSortDirection } from '../../models/fileDocument.model';
import { Contact } from '../../models/contact.model';
import randomString from '../../utils/randomString';

/**
 * @internal
 */
export const FILEDOCUMENT_SVC = 'FileDocumentService';

/**
 * @eventProperty
 * Events fired by the DocumentsService
 */
export enum FileDocumentServiceEvents {
    /**
     * @eventProperty
     * This event is fired when a fileDocument is added in the storage
     */
    ON_FILE_DOCUMENT_UPLOADED = "ON_FILE_DOCUMENT_UPLOADED",

    /**
     * @eventProperty
     * This event is fired when a fileDocument is updated (state change or viewer added/removed) in the storage
     */
    ON_FILE_DOCUMENT_UPDATED = "ON_FILE_DOCUMENT_UPDATED",

    /**
     * @eventProperty
     * This event is fired when a fileDocument is removed from the storage
     */
    ON_FILE_DOCUMENT_REMOVED = "ON_FILE_DOCUMENT_REMOVED",

    /**
     * @eventProperty
     * This event is fired when fileDocument storage consumption is updated
     */
    ON_CONSUMPTION_UPDATED = "ON_CONSUMPTION_UPDATED",

    /**
     * @eventProperty
     * This event is fired when fileDocument storage usage exceeds 90%.
     */
    ON_CONSUMPTION_WARNING = "ON_CONSUMPTION_WARNING",

    /**
    * @eventProperty
    * This event is fired when the content of the document is modified
    */
    ON_FILE_DOCUMENT_CONTENT_MODIFIED = "ON_FILE_DOCUMENT_CONTENT_MODIFIED"
}

export interface FileDocumentConsumptionData extends ConsumptionData {
    /** @internal */
    option?: any;
}
export interface FileDocumentService {

    /**
     * This property contains the current usage status of your fileDocument storage.
     */
    consumption?: FileDocumentConsumptionData;

    /**
     * Subscribe to updates from the service (all events are of RBEvent);
     * @param handler - The call-back function that will be subscribed to the RxJS subject
     * @param eventNames - array of event to listen
     */
    subscribe(handler: (event: RBEvent<FileDocumentServiceEvents>) => any, eventNames?: FileDocumentServiceEvents | FileDocumentServiceEvents[]): Subscription;

    /**
     * This factory allows to create Document instance from a JavaScript File
     * @param file - the JavaScript file to manage
     * @throws an RBError (ex: RBErrorCode.STORAGE_NOT_ENOUGH_SPACE_AVAILABLE)
     */
    createFileDocument(file: File): Promise<FileDocument>;

    /**
     * This method allows to replace the document content.
     * @param document - The file descriptor object.
     * @param data - the content blob
     */
    replaceFileDocumentContent(document: FileDocument, data: Blob): void;

    /**
     * This method allows you to search for fileDocuments in your fileDocument storage.
     * The main search criterion is by name, but you can also leave this field empty
     * and use the criteria contained in search options
     * @param options - Search options
     */
    searchFileDocuments(options?: FileDocumentSearchOptions): Promise<FileDocument[]>;

    /**
     * This method retrieves the fileDocuments shared in a conversation.
     * For obvious performance reasons, it does not return the full list of fileDocuments, but a page of 1000 fileDocuments.
     * To retrieve the next page, simply call it up again.
     * @param conversation - the conversation
     */
    getConversationFileDocuments(conversation: Conversation): Promise<{ documents: FileDocument[]; complete: boolean; }>;

    /**
     * This method retrieves the fileDocuments shared in a conversation.
     * For obvious performance reasons, it does not return the full list of fileDocuments, but a page of 1000 fileDocuments.
     * To retrieve the next page, simply call it up again.
     * @param conversation - the conversation
     */

    /**
     * This method will create an archive (zip) will all fileDocuments; Once generated, the zip will be available in the user storage;
     * ATTENTION: In return you'll get the FileDocument that can be used to download the archive, but it will remain in the user storage; So consider removing it from the storage in order to save space;
     * ATTENTION2: Each call of this API will generate a new zip that will be storred in the storage of the user.
     * NB: This process might take a while, up to several minutes (depending on the files size, etc)
     * @param zipName - the name of the zip [Optional]
     * @param documents - the list of documents to zip
     */
    createZipFromDocuments(zipName: string, documents: FileDocument[]): Promise<FileDocument>;

    /**
     * Retrieves a {@link FileDocument} instance from the local cache by its identifier.
     *
     * **Legacy compatibility** — This method exists to address legacy behaviours where a
     * `FileDocument` object could not be obtained through the standard asynchronous APIs
     * (e.g. {@link searchFileDocuments}, {@link getConversationFileDocuments}).
     * It performs a **synchronous** lookup against the in-memory document registry and
     * should therefore only be used when the document is already known to be cached locally.
     * Prefer the async APIs for any new development.
     *
     * @param fileDocumentId - The unique identifier of the file document to retrieve.
     * @returns The matching {@link FileDocument}, or `undefined` if not found in the local cache.
     */
    getFileDocumentById(fileDocumentId: string): FileDocument;
}

/** @internal */
export class FileDocumentServiceRB extends Service implements FileDocumentService {

    private logger: LoggerService = LoggerService.getInstance();
    private fileStorage: FileStorageService = FileStorageService.getInstance();
    private fileServer: FileServerService = FileServerService.getInstance();
    private eventSubject: Subject<RBEvent<FileDocumentServiceEvents>> = new Subject<RBEvent<FileDocumentServiceEvents>>();
    private subscriptions?: Subscription[];
    private documents: Record<string, FileDocument> = {};

    public static getInstance() {
        return Service.create(FILEDOCUMENT_SVC, FileDocumentServiceRB);
    }

    public static build() {
        return new FileDocumentServiceRB();
    }

    private constructor() {
        super();
    }

    public async start(): Promise<void> {
        this.attachEventHandler();
    }

    public async reconnect(): Promise<void> {
        this.attachEventHandler();
    }

    public async stop(): Promise<void> {
        this.subscriptions?.forEach((subscription) => { subscription.unsubscribe(); subscription = undefined; });
    }

    private attachEventHandler(): void {
        this.logger.info(`[FileDocumentService] attachEventHandler()`);
        this.subscriptions?.forEach((subscription) => { subscription.unsubscribe(); subscription = undefined; });
        this.subscriptions = [];
        this.subscriptions?.push(this.fileStorage.subscribe(async (event: RBEvent) => {
            switch (event.name) {
                case FileStorageServiceEvents.ON_FILE_DESCRIPTOR_RECEIVED_EVENT:
                    const document = this.documents[event.data.fileId];
                    if (document) this.sendEvent(FileDocumentServiceEvents.ON_FILE_DOCUMENT_UPDATED, document);
                    else {
                        const addedDocument = await this.getFileDocumentById(event.data.fileId) as FileDocumentRB;
                        addedDocument.sendEvent(FileDocumentEvents.ON_UPLOADED);
                        this.sendEvent(FileDocumentServiceEvents.ON_FILE_DOCUMENT_UPLOADED, addedDocument);
                    }
                    break;
                case FileStorageServiceEvents.ON_FILE_REMOVED_FROM_STORE_EVENT:
                    delete this.documents[event.data.fileId];
                    this.sendEvent(FileDocumentServiceEvents.ON_FILE_DOCUMENT_REMOVED);
                    break;
                case FileStorageServiceEvents.ON_CONSUMPTION_DATA_UPDATED_EVENT:
                    this.sendEvent(FileDocumentServiceEvents.ON_CONSUMPTION_UPDATED, event.data);
                    break;
                case FileStorageServiceEvents.ON_CONSUMPTION_DATA_WARNING_EVENT:
                    this.sendEvent(FileDocumentServiceEvents.ON_CONSUMPTION_WARNING, event.data);
                    break;
                default: break;
            }
        }));

        // TODO: MCO : Refactor fileDescriptorsSubject with RBEvents
        this.subscriptions?.push(this.fileStorage.fileDescriptorsSubject.subscribe(async (event: any) => {
            switch (event.action) {
                case 'update':
                    let document: FileDocumentRB = this.documents[event.fd.id] as FileDocumentRB; ;
                    if (!document) document = await this.getFileDocumentById(event.fd.id) as FileDocumentRB;
                    document.sendEvent(FileDocumentEvents.ON_CONTENT_MODIFIED);
                    this.sendEvent(FileDocumentServiceEvents.ON_FILE_DOCUMENT_CONTENT_MODIFIED, document);
                    break;
                default: break;
            }
        }));
    }

    public subscribe(handler: (event: RBEvent<FileDocumentServiceEvents>) => any, eventNames?: FileDocumentServiceEvents | FileDocumentServiceEvents[]): Subscription {
        const observable = eventNames
            ? this.eventSubject.pipe(filter((event: RBEvent<FileDocumentServiceEvents>) => {
                if (eventNames instanceof Array) return eventNames.indexOf(event.name) !== -1;
                return event.name === eventNames;
            }))
            : this.eventSubject;

        return observable.subscribe((event: RBEvent<FileDocumentServiceEvents>) => {
            try { handler(event); }
            catch (error) { this.logger.error(`[FileDocumentService] subscribe () -- error -- ${error.message}`); }
        });
    }

    public sendEvent(name: FileDocumentServiceEvents, data?: any): void {
        this.eventSubject.next(RBEvent.create(name, data));
    }

    public async createFileDocument(file: File): Promise<FileDocument> {
        const fd = await this.fileStorage.createFileDescriptor(file);
        if (!this.fileStorage.isEnoughAvailableSpaceForFiles([fd.id])) {
            throw RBError.create("Not enough space available on storage", RBErrorCode.STORAGE_NOT_ENOUGH_SPACE_AVAILABLE);
        }
        fd.fileToSend = file;
        return this.createDocumentFromFileDescriptor(fd);
    }

    public replaceFileDocumentContent(document: FileDocument, data: Blob): void {
        (document as FileDocumentRB).fd.fileToSend = new File([data], document.name, { type: data.type });
    }

    public async createFileDocumentFromExisitingFileDescriptor(fd: FileDescriptor, file: File): Promise<FileDocument> {

        if (!this.fileStorage.isEnoughAvailableSpaceForFiles([fd.id])) {
            throw RBError.create("Not enough space available on storage", RBErrorCode.STORAGE_NOT_ENOUGH_SPACE_AVAILABLE);
        }
        fd.fileToSend = file;
        return this.createDocumentFromFileDescriptor(fd);
    }

    public hasEnoughSpace(document: FileDocument): boolean {
        this.logger.info(`[FileDocumentService] hasEnoughSpace(document: ${document.id})`);
        return this.fileStorage.isEnoughAvailableSpaceForFiles([document.fileDescriptor.id]);
    }

    public async uploadDocument(document: FileDocument): Promise<FileDocument> {
        try {
            const documentRB = document as FileDocumentRB;
            this.logger.info(`[FileDocumentService] uploadDocument(document: ${document.id})`);
            const fileDescriptor: FileDescriptor = documentRB.fileDescriptor;
            documentRB.handleTransferProgress();
            await this.fileServer.uploadFile(fileDescriptor);
            return document;
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] uploadDocument(document: ${document.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async downloadDocument(document: FileDocument): Promise<Blob> {
        try {
            const documentRB = document as FileDocumentRB;
            this.logger.info(`[FileDocumentService] downloadDocument(document: ${document.id})`);
            documentRB.handleTransferProgress();
            return await this.fileServer.getBlobFromFileDescriptor(documentRB.fileDescriptor, true);
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] downloadDocument(document: ${document.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async abortDocumentTransfert(document: FileDocument): Promise<FileDocument> {
        const documentRB: FileDocumentRB = document as FileDocumentRB;
        this.logger.info(`[FileDocumentService] abortDocumentTransfert(document: ${document.id})`);
        this.fileServer.cancelCurrentFiletransfer(documentRB.fileDescriptor.id);
        documentRB.transfertProgressSubscription?.unsubscribe();
        documentRB.transfertProgressSubscription = undefined;
        return document;
    }

    public async copyDocument(document: FileDocument): Promise<FileDocument> {
        try {
            const documentRB = document as FileDocumentRB;
            this.logger.info(`[FileDocumentService] copyDocument(document: ${document.id})`);
            const copiedFileDescriptor = await this.fileStorage.copyFileToUserCloudStorage(documentRB.fileDescriptor.id);
            const copiedDocument = this.createDocumentFromFileDescriptor(copiedFileDescriptor);
            return copiedDocument;
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] copyDocument(document: ${document.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async deleteDocument(document: FileDocument): Promise<void> {
        try {
            this.logger.info(`[FileDocumentService] deleteDocument(document: ${document.id})`);
            await this.fileStorage.deleteFileDescriptor(document.fileDescriptor.id);
            return;
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] deleteDocument(document: ${document.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public get consumption(): FileDocumentConsumptionData { return this.fileStorage.getConsumptionData(); }

    public async addDocumentViewer(document: FileDocument, viewer: User | Bubble): Promise<void> {
        try {
            const documentRB = document as FileDocumentRB;
            const type = (viewer instanceof Contact) ? 'user' : 'room';
            this.logger.info(`[FileDocumentService] addDocumentViewer(${viewer.dbId})`);
            const fd = await this.fileStorage.addFileViewer(document.fileDescriptor.id, viewer.dbId, type);
            documentRB.fd = fd;
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] addDocumentViewer(${viewer.dbId}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async deleteDocumentViewer(document: FileDocument, viewer: User | Bubble): Promise<void> {
        try {
            const documentRB = document as FileDocumentRB;
            this.logger.info(`[FileDocumentService] deleteViewer(${viewer.dbId})`);
            const fd = await this.fileStorage.deleteFileViewer(viewer.dbId, documentRB.fileDescriptor.id);
            documentRB.fd = fd;
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] deleteViewer(${viewer.dbId}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async searchFileDocuments(searchOptions?: FileDocumentSearchOptions): Promise<FileDocument[]> {
        try {
            this.logger.info(`[FileDocumentService] searchDocuments()`);
            const nameFilter = searchOptions?.name ? searchOptions.name : '';
            const documentSet = searchOptions?.documentSet ? searchOptions.documentSet : FileDocumentSearchSet.ALL;
            const mime = searchOptions?.mime ? searchOptions.mime : '';
            const sortBy = searchOptions?.sortBy ? searchOptions.sortBy : FileDocumentSearchSortBy.UPLOADED_DATA;
            const sortDirection = searchOptions?.sortDirection ? searchOptions.sortDirection : FileDocumentSearchSortDirection.DOWN;
            const results = await this.fileStorage.searchForFilesOnServer(documentSet, nameFilter, mime, sortBy, sortDirection, 0, true, true);
            return results.fileDescriptors.map(fd => this.createDocumentFromFileDescriptor(fd));
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] searchDocuments() -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async createZipFromDocuments(zipName: string = '', documents: FileDocument[]): Promise<FileDocument> {
        if (zipName.indexOf(".zip") === -1) { zipName += ".zip"; }
        this.logger.info(`[FileDocumentService] createZipFromDocuments() with name ${zipName}`);
        return new Promise((resolve, reject) => {
            let listener;
            let documentId;
            try {
                //wait for the file descriptor to be ready
                listener = this.fileStorage.subscribe((event: RBEvent) => {
                    //we get the file descriptor, now we can decice what to do with it;
                    //in case of ucaas, we need to download it and delete it after that;
                    //this will be managed rooms.angular service; For SDK, we will return the FileDocument created and leave the user do what he wants;
                    if (documentId === event.data.fileDescriptor?.id) {
                        const document = this.createDocumentFromFileDescriptor(event.data.fileDescriptor);
                        listener.unsubscribe();
                        resolve(document);
                    }
                }, FileStorageServiceEvents.ON_ARCHIVE_AVAILABLE);

                this.fileStorage?.createZipFromFiles(zipName, documents)
                    .then((fileId) => { documentId = fileId; });
            }
            catch (error) {
                listener?.unsubscribe();
                listener = undefined;
                reject(new Error(`[FileDocumentService] createZipFromDocuments error -- ${error?.message}`));
            }
        });
    }

    public async getConversationFileDocuments(conversation: Conversation): Promise<{ documents: FileDocument[]; complete: boolean; }> {
        try {
            this.logger.info(`[FileDocumentService] getConversationDocuments(conv: ${conversation.id})`);
            const results = await this.fileStorage.loadMoreDescriptorsForConversation(conversation as ConversationRB);
            const documents = results.fileDescriptors.map(fd => this.createDocumentFromFileDescriptor(fd));
            const complete = results.allReceived;
            return { documents, complete };
        }
        catch (error) {
            this.logger.error(`[FileDocumentService] getConversationDocuments(conv: ${conversation.id}) -- failure -- ${error?.message}`);
            throw error;
        }
    }

    public async getLocalUrl(fileDocument: FileDocument): Promise<string> {
        return await this.fileServer.getFileDescriptorUrl(fileDocument.fileDescriptor);
    }

    public async getPublicUrl(fileDocument: FileDocument): Promise<string> {
        return await this.fileServer.getFileDescriptorPublicUrl(fileDocument.fileDescriptor);
    }

    public async getTextContent(fileDocument: FileDocument, noCache: boolean = false): Promise<string> {
        if (!fileDocument) return undefined;
        let url = await fileDocument.getPublicUrl();
        if (noCache) url += `${url.indexOf("?") !== -1 ? '?' : '&'}rnd=${randomString(10)}`;
        const response: Response = await fetch(url, { method: 'GET' });
        if (!response.ok) throw (response);
        return await response.text();
    }

    public async getJsonContent<T = any>(fileDocument: FileDocument): Promise<T> {
        if (!fileDocument) return undefined;
        if (fileDocument.mime !== 'application/json') throw new Error('mimeType is not "application/json"');
        const url = await fileDocument.getPublicUrl();
        const response: Response = await fetch(url, { method: 'GET' });
        if (!response.ok) throw (response);
        return await response.json() as T;
    }

    /* ***************************************************** */
    /* ** INTERNAL TOOLS                                  ** */
    /* ***************************************************** */

    public async getFileDescriptorUrl(fd: FileDescriptor): Promise<string> {
        return this.fileServer.getFileDescriptorUrl(fd, true, true);
    }

    public getFileDocumentById(fileDocumentId: string): FileDocument {
        if (this.documents[fileDocumentId]) return this.documents[fileDocumentId];
        const fd = this.fileStorage.getFileDescriptorById(fileDocumentId);
        return this.createDocumentFromFileDescriptor(fd);
    }

    public createDocumentFromFileDescriptor(fd: FileDescriptor): FileDocumentRB {
        const document = new FileDocumentRB();
        document.fd = fd;
        document.handleThumbnail();
        this.documents[document.id] = document;
        return document;
    }
}

/*
public getLatestFilesFromServer()

// OK public async retrieveReceivedFilesForNewRoom(roomId: string): Promise<void> {
// OK public async loadMoreDescriptorsForConversation(conversation: ConversationRB): Promise<GetFilesResultInterface> {
// OK public async getFileDescriptorsInConversation(conversation: ConversationRB): Promise<GetFilesResultInterface> {
// OK public async searchForFilesOnServer(fileSet: string = "all", fileName: string = "", typeMIME: string = "", sortField: string = "uploadedDate", sortOrder: number = -1, offset: number = 0, force: boolean = false, onlyInternal: boolean = false): Promise<GetFilesResultInterface> {
// OK public async copyFileToUserCloudStorage(fileId: string): Promise<FileDescriptor> {
public stopAllFileTransfer(): void {

// OK public getMimeInfo(mimeType: string): any {
// OK public isEnoughAvailableSpaceForFiles(filesToSend: any[]): boolean {
// OK public isEnoughAvailableSpaceForFileDescriptor(fileId: string, fileViewers: any): boolean {

// FILE VIEWER

// OK public async addFileViewer(fileId: string, viewerId: string, type: string): Promise<FileDescriptor> {
// OK public removeRoomFileViewer(room: BubbleRB): void {
// OK public async deleteFileViewer(viewerId: string, fileId: string): Promise<any> {

// CONSUMPTION

// OK public async retrieveUserConsumption(): Promise<ConsumptionData> {
// OK public getConsumptionData(): ConsumptionData { return this.consumptionData; }

// VOICE MESSAGES

public getVoiceMessageFileDescriptorByMsgId(msgId: string): FileDescriptor {
public async retrieveVoiceMessageFileDescriptorsListPerOwner(): Promise<FileDescriptor[]> {

// FILE DESCRIPTORS

// OK public getFileDescriptorById(id: string): FileDescriptor {
// OK public removeFileDescriptorById(id: string): any {
// OK public async createFileDescriptor(file: File, viewers: FileViewer[] = [], voiceMessageData = null, ccarelogs: boolean = false, ccareclientlogs: boolean = false): Promise<FileDescriptor> {
// OK public async deleteFileDescriptor(id: string) {
public async deleteAllFileDescriptor() {
public retrieveOneFileDescriptor(fileId: string): Promise<FileDescriptor> {
public retrieveAndStoreOneFileDescriptor(fileId: string, forceRetrieve: boolean = false): Promise<FileDescriptor> {

// ***************************************************************************
// FILESERVER

public async getFileDescriptorPublicUrl(fileDescriptor: FileDescriptor): Promise<string> {
// OK public async getFileDescriptorUrl(fileDescriptor: FileDescriptor, thumbnail: boolean = false, cacheEnabled: boolean = false): Promise<string> {
public removeFileDescriptorFromCache(fileDescriptor: FileDescriptor) {
public async clearCache() {
public async getLocalImage(fileDescriptorId: string, thumbnail: boolean, forced: boolean = true): Promise<LocalImage> {
public async getBlobThumbnailFromFileDescriptor(fileDescriptor: FileDescriptor): Promise<Blob> {
public async getBlobFromFileDescriptor(fileDescriptor: FileDescriptor, cancelable: boolean = true, actionOnPartialResponse: Function = null): Promise<Blob> {
public async getBlobFileDescriptorFromUrl(url: string, fileDescriptor: FileDescriptor): Promise<Blob> {
public async getBlobUrlAndOrientationByFileDescriptorId(id: string): Promise<any> {
public setImageOrientationForFileDescriptor(fileDescriptor: FileDescriptor, blob: Blob): Promise<any> {
// OK public async uploadAFileByChunk(fileDescriptor: FileDescriptor, file: File): Promise<any> {
// OK public async uploadFile(fileDescriptor: FileDescriptor): Promise<FileDescriptor> {
// OK public cancelCurrentFiletransfer(fileDescriptorId: string) {

public async getServerCapabilities(): Promise<FileServerCapabilities> {
public getFileDescriptorCapability(fileDescriptor: FileDescriptor, capability: FileStorageCapabilities): boolean {

*/
